import {
  ACCOUNT_CATEGORY,
  ACCOUNT_TYPE,
  AddressPreferences,
  BaseConfig,
  BaseController,
  BaseEmbedController,
  BaseEmbedControllerState,
  BROADCAST_CHANNELS,
  CHAIN_NAMESPACES,
  COMMUNICATION_NOTIFICATIONS,
  CommunicationWindowManager,
  CONFIRMATION_STRATEGY,
  createIdleTimeTrackerMiddleware,
  createLoggerMiddleware,
  createOriginMiddleware,
  DEFAULT_PREFERENCES,
  FEATURES_CONFIRM_WINDOW,
  getPopupFeatures,
  type ICommunicationProviderHandlers,
  IdleTimeTracker,
  PopupHandler,
  PopupWithBcHandler,
  PROVIDER_NOTIFICATIONS,
  randomId,
  SetIFrameStatusParams,
  ShowFundingParams,
  THEME,
  UserInfo,
  UserRequestApprovalParams,
  WindowBlockAlertParams,
} from "@toruslabs/base-controllers";
import { isStrictHexString } from "@toruslabs/metadata-helpers";
import {
  type Auth,
  AUTH_CONNECTION_TYPE,
  type AuthConnectionConfig,
  createEngineStreamV2,
  HttpClient,
  JRPCEngineV2,
  JRPCMiddlewareV2,
  JRPCRequest,
  JsonRpcCall,
  providerAsMiddlewareV2,
  SafeEventEmitter,
  SafeEventEmitterProvider,
  Substream,
  THEME_MODE_TYPE,
  THEME_MODES,
  WEB3AUTH_NETWORK_TYPE,
} from "@web3auth/auth";
import { ethereumStrategy, SIWWeb3, solanaStrategy } from "@web3auth/sign-in-with-web3";
import { cloneDeep } from "lodash-es";
import log from "loglevel";
import { pipeline } from "readable-stream";

import useUtils from "../composables/use-utils";
import appConfig from "../config";
import AuthFactory from "../handlers/Auth/AuthFactory";
import AuthWindowHandler from "../handlers/Auth/AuthWindowHandler";
import { SOLANA_WALLET_DAPP_NAME } from "../utils/constants";
// TODO: Move this to Auth SDK
import { SIGN_CONFIRM, type SIGN_CONFIRM_TYPE, TORUS_CONTROLLER_EVENTS } from "../utils/enums";
import {
  APP_PLATFORMS,
  type APP_PLATFORMS_TYPE,
  AuthPopupResponse,
  GasFeeModalData,
  LOGIN_MODE,
  type LOGIN_MODE_TYPE,
  MessageChannelEventData,
} from "../utils/interfaces";
import { generatePubKeyFromPrivateKey, isMain, isSwapAllowed } from "../utils/utils";
import { ITorusControllerConfig, ITorusControllerEvents, ITorusControllerState } from "./interfaces";
import { TokenProviderAdapter } from "./TokenProviderAdapter";

const AUTH_JRPC_METHODS = {
  NEED_ACCESS_TOKEN: "need_access_token",
  ACCESS_TOKEN_RESULT: "access_token_result",
} as const;

SIWWeb3.registerStrategy(ethereumStrategy);
SIWWeb3.registerStrategy(solanaStrategy);

const {
  getSessionIdFromStorage,
  getUserThemeFromStorage,
  loginMode,
  sessionNamespace: rootSessionNamespace,
  setNamespace,
  setLoginMode,
  addDashboardApplication,
} = useUtils();

const getLoginErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string" && error) return error;

  if (error && typeof error === "object") {
    const errorObject = error as {
      status?: unknown;
      statusText?: unknown;
      message?: unknown;
      constructor?: { name?: string };
    };

    if (typeof errorObject.message === "string" && errorObject.message) {
      return errorObject.message;
    }

    if (typeof errorObject.status === "number") {
      const statusText = typeof errorObject.statusText === "string" && errorObject.statusText ? ` ${errorObject.statusText}` : "";
      return `Login failed with status ${errorObject.status}${statusText}`;
    }

    if (errorObject.constructor?.name) {
      return `Login failed: ${errorObject.constructor.name}`;
    }
  }

  return "Login failed";
};

// Fired right before the provider emits `chainChanged`. Used by cross-namespace
// flows that must not continue until the provider-facing chain change phase starts.
export const CONTROLLER_PROVIDER_CHAIN_CHANGED_EVENT = "CONTROLLER_PROVIDER_CHAIN_CHANGED_EVENT";
// Fired when the controller has switched to a new active network. Used by
// same-namespace switch flows to resolve as soon as the target network is active.
export const CONTROLLER_ACTIVE_NETWORK_CHANGED_EVENT = "CONTROLLER_ACTIVE_NETWORK_CHANGED_EVENT";
// Fired when the underlying provider reports a switch-related error.
export const CONTROLLER_PROVIDER_ERROR_EVENT = "CONTROLLER_PROVIDER_ERROR_EVENT";

export default abstract class AbstractTorusController<C extends ITorusControllerConfig, S extends ITorusControllerState> extends BaseController<
  C,
  S,
  ITorusControllerEvents
> {
  public communicationWindowManager = new CommunicationWindowManager();

  public engine?: JRPCEngineV2;

  public httpClient: HttpClient;

  public idleTimeTracker: IdleTimeTracker;

  // this method will be set by controllerStore as it may involve multiple chains
  public switchChain: (chainId: string) => Promise<void>;

  public providerNotificationEmitter = new SafeEventEmitter();

  public communicationNotificationEmitter = new SafeEventEmitter();

  protected communicationEngine?: JRPCEngineV2;

  protected embedController!: BaseEmbedController<BaseConfig, BaseEmbedControllerState>;

  protected isRehydrated = false;

  // ACCESS_TOKEN_RESULT resolves against the controller that receives the
  // communication notification, so plugin-mode token requests cannot survive
  // being split across old/new controller instances.
  private _pendingTokenRequests = new Map<string, { resolve: (token: string) => void; reject: (error: Error) => void }>();

  private providerChainChangedConfig?: { chainId: string; chainNamespace: string };

  public abstract selectedAddress: string;

  public abstract selectedAddresses: string[];

  public abstract provider: SafeEventEmitterProvider;

  get userInfo(): UserInfo {
    // Use only for UI
    return this.getAccountPreferences(this.selectedAddress)?.userInfo || cloneDeep(DEFAULT_PREFERENCES.userInfo);
  }

  public hasStartedProviderChainChange(targetChainConfig: { chainId: string; chainNamespace: string }): boolean {
    return (
      this.providerChainChangedConfig?.chainId === targetChainConfig.chainId &&
      this.providerChainChangedConfig?.chainNamespace === targetChainConfig.chainNamespace
    );
  }

  public async triggerLogin({
    authConnection,
    authConnectionId,
    groupedAuthConnectionId,
    login_hint: loginHint,
    theme,
    preOpenedWindowId,
  }: {
    authConnection: AUTH_CONNECTION_TYPE;
    authConnectionId?: string;
    groupedAuthConnectionId?: string;
    login_hint?: string;
    theme: THEME;
    preOpenedWindowId?: string;
  }): Promise<AuthPopupResponse & { isUserSkippedMfa: boolean }> {
    try {
      const whiteLabel =
        !this.state.EmbedControllerState.gating.allowWalletService || !this.state.EmbedControllerState.whiteLabel
          ? {
              mode: theme,
            }
          : this.state.EmbedControllerState.whiteLabel;
      const authConnectionConfig = this.getAuthConnectionConfig();
      const loginMethodConfig = this.state.EmbedControllerState.loginMethodConfig;
      const authConnectionConfigItem = loginMethodConfig[authConnection];
      const web3AuthClientId = this.getWeb3AuthClientId(authConnection);
      const web3AuthNetwork = this.getWeb3AuthNetwork(authConnection);
      const extraLoginOptions = {
        ...(authConnectionConfigItem?.extraLoginOptions || {}),
        ...(loginHint ? { login_hint: loginHint } : {}),
      };
      // Use the pre-opened windowId from ws-embed if available, otherwise generate a new one
      const windowId = preOpenedWindowId || randomId();
      const handler = new AuthWindowHandler({
        windowId,
        authConnection,
        extraLoginOptions,
        web3AuthClientId,
        web3AuthNetwork,
        authConnectionId: authConnectionId || authConnectionConfigItem?.authConnectionId,
        groupedAuthConnectionId: groupedAuthConnectionId || authConnectionConfigItem?.groupedAuthConnectionId,
        authConnectionConfig,
        whiteLabel,
        sessionNamespace: rootSessionNamespace.value,
        isWindowAlreadyOpen: !!preOpenedWindowId,
        loginMode: loginMode.value,
        // provide the embed dapp client id to the auth window handler if the login mode is embed
        ...(loginMode.value === LOGIN_MODE.embed ? { embedDappClientId: this.state.EmbedControllerState.web3AuthClientId } : {}),
      });
      const result = await handler.handleLoginWindow({
        communicationEngine: this.communicationEngine,
        communicationWindowManager: this.communicationWindowManager,
        communicationNotificationEmitter: this.communicationNotificationEmitter,
        handleWindowBlockAlert: this.handleWindowBlockAlert.bind(this),
      });
      const { sessionId, accessToken, refreshToken, idToken } = result;
      const auth = await AuthFactory.getInstance({
        web3AuthClientId,
        web3AuthNetwork,
        // authConnectionConfig,
        rehydrate: true,
        sessionId,
        accessToken,
        refreshToken,
        idToken,
        sessionNamespace: rootSessionNamespace.value,
        replaceUrlOnRedirect: true,
      });
      if (!auth.sessionId) {
        throw new Error("Auth session could not be rehydrated after login");
      }
      this.httpClient = this.createHttpClient(auth);
      const userInfo = await AuthFactory.getUserInfo();
      const data = await AuthFactory.getKeysInfo();

      const mainAddresses = await Promise.all(
        data.keys.map(async (key) => {
          const address = await this.addAccount({
            privKey: key.privKey.padStart(64, "0"),
            userInfo,
            rehydrate: false,
            sessionPubKey: generatePubKeyFromPrivateKey(sessionId),
            type: key.accountType,
            loginMode: loginMode.value,
            signatures: data.signatures,
          });
          return {
            accountType: key.accountType,
            address,
          };
        })
      );

      let selectedAddress = mainAddresses.filter((x) => !!x.address)[0]?.address;

      const thresholdAccount = mainAddresses.find((key) => key.accountType === ACCOUNT_CATEGORY.THRESHOLD);
      if ((loginMode.value === LOGIN_MODE.main || loginMode.value === LOGIN_MODE.embed) && thresholdAccount) {
        const { userDapps, keys: extraKeys } = await AuthFactory.getUserDapps({
          postboxKey: data.postboxKey,
          chainNamespace: this.state.NetworkControllerState.providerConfig.chainNamespace,
          httpClient: this.httpClient,
        });

        await Promise.all(
          extraKeys.map(async (key) => {
            const accountType =
              userDapps[key.address] && key.accountType === ACCOUNT_CATEGORY.APP_SCOPED ? ACCOUNT_CATEGORY.APP_SCOPED_DERIVED : key.accountType;

            const address = await this.addAccount({
              privKey: key.privKey.padStart(64, "0"),
              userInfo: {
                ...userInfo,
                name: userDapps[key.address] || userInfo.name,
              },
              rehydrate: true,
              sessionPubKey: generatePubKeyFromPrivateKey(sessionId), // TODO: check if Solana needs this
              type: accountType,
              signatures: data.signatures,
              mainAddress: thresholdAccount?.address,
            });

            if (userDapps[key.address] === SOLANA_WALLET_DAPP_NAME) {
              selectedAddress = address;
            }
          })
        );
      }

      this.setSelectedAccount(selectedAddress);
      this.emit(TORUS_CONTROLLER_EVENTS.LOGIN_RESPONSE, null, this.selectedAddresses, sessionId);
      return {
        ...result,
        isUserSkippedMfa: AuthFactory.isUserSkippedMfa(data.keys),
      };
    } catch (error) {
      this.emit(TORUS_CONTROLLER_EVENTS.LOGIN_RESPONSE, getLoginErrorMessage(error));
      log.error(error);
      throw error;
    }
  }

  public async connectWithSession(sessionString: string, idToken: string): Promise<{ success: boolean; isUserSkippedMfa: boolean }> {
    log.debug("login with session: request params");
    if (!sessionString) throw new Error("sessionString is required");
    if (!idToken) throw new Error("idToken is required");
    const [sessionId, sessionNamespace] = sessionString.split(":");
    if (!sessionId) throw new Error("sessionId is required");
    if (sessionNamespace) setNamespace(sessionNamespace);
    const auth = await AuthFactory.getInstance({
      web3AuthClientId: this.getWeb3AuthClientId(),
      web3AuthNetwork: this.getWeb3AuthNetwork(),
      rehydrate: true,
      sessionId,
      sessionNamespace,
      replaceUrlOnRedirect: false,
      idToken,
      accessTokenProvider: loginMode.value === LOGIN_MODE.plugin ? () => this.requestAccessTokenFromDapp() : undefined,
    });
    this.httpClient = this.createHttpClient(auth);
    const userInfo = await AuthFactory.getUserInfo();
    const data = await AuthFactory.getKeysInfo();
    const addresses = await Promise.all(
      data.keys.map(async (key) => {
        return this.addAccount({
          privKey: key.privKey.padStart(64, "0"),
          userInfo,
          rehydrate: false,
          type: key.accountType,
          sessionPubKey: generatePubKeyFromPrivateKey(sessionId),
          loginMode: loginMode.value,
          signatures: data.signatures,
        });
      })
    );
    this.setSelectedAccount(addresses.filter((x) => !!x)[0]);

    // Add dapp-scoped accounts if any, this is used by Auth dashboard
    const mainAddress = addresses[0];
    const { userDapps, keys: extraKeys } = await AuthFactory.getAuthDashboardDapps();
    await Promise.all(
      extraKeys.map(async (key) => {
        const accountType =
          userDapps[key.address] && key.accountType === ACCOUNT_CATEGORY.APP_SCOPED ? ACCOUNT_CATEGORY.APP_SCOPED_DERIVED : key.accountType;

        const dappAddress = await this.addAccount({
          privKey: key.privKey.padStart(64, "0"),
          userInfo: {
            ...userInfo,
            name: userDapps[key.address] || userInfo.name,
          },
          rehydrate: true,
          type: accountType,
          signatures: data.signatures,
          mainAddress,
        });

        key.dappIds.forEach((dappId) => {
          addDashboardApplication({ dappId, address: dappAddress });
        });

        return dappAddress;
      })
    );

    this.providerNotificationEmitter.emit("notification", {
      method: PROVIDER_NOTIFICATIONS.UNLOCK_STATE_CHANGED,
      params: {
        accounts: this.selectedAddresses,
        isUnlocked: true,
      },
    });
    this.notifyIsLoggedIn();
    return { success: true, isUserSkippedMfa: AuthFactory.isUserSkippedMfa(data.keys) };
  }

  // This rehydrate is being called on switch chain
  public async rehydrate(): Promise<{ success: boolean; isUserSkippedMfa: boolean }> {
    try {
      this.isRehydrated = true;
      // Main/embed mode can safely reuse the shared AuthFactory singleton.
      // Plugin mode cannot, because its accessTokenProvider is bound to the
      // current controller instance via requestAccessTokenFromDapp().
      const shouldRehydrateAuth = loginMode.value === LOGIN_MODE.plugin;
      const auth = await AuthFactory.getInstance({
        web3AuthClientId: this.getWeb3AuthClientId(),
        web3AuthNetwork: this.getWeb3AuthNetwork(),
        rehydrate: shouldRehydrateAuth,
        sessionNamespace: rootSessionNamespace.value,
        replaceUrlOnRedirect: true,
        // In plugin mode, AuthFactory keeps a singleton Auth instance. Recreate it
        // on namespace switch so its accessTokenProvider points at the current controller.
        accessTokenProvider: shouldRehydrateAuth ? () => this.requestAccessTokenFromDapp() : undefined,
      });
      const { sessionId } = auth;
      this.httpClient = this.createHttpClient(auth);
      const userInfo = await AuthFactory.getUserInfo();
      const data = await AuthFactory.getKeysInfo();

      const mainAddresses = await Promise.all(
        data.keys.map(async (key) => {
          const address = await this.addAccount({
            privKey: key.privKey.padStart(64, "0"),
            userInfo,
            rehydrate: true,
            sessionPubKey: generatePubKeyFromPrivateKey(sessionId), // TODO: check if Solana needs this
            type: key.accountType,
            loginMode: loginMode.value,
            signatures: data.signatures,
          });
          return {
            accountType: key.accountType,
            address,
          };
        })
      );

      let selectedAddress = mainAddresses.filter((x) => !!x.address)[0]?.address;

      const thresholdAccount = mainAddresses.find((key) => key.accountType === ACCOUNT_CATEGORY.THRESHOLD);
      if ((loginMode.value === LOGIN_MODE.main || loginMode.value === LOGIN_MODE.embed) && thresholdAccount) {
        const { userDapps, keys: extraKeys } = await AuthFactory.getUserDapps({
          postboxKey: data.postboxKey,
          chainNamespace: this.state.NetworkControllerState.providerConfig.chainNamespace,
          httpClient: this.httpClient,
        });

        await Promise.all(
          extraKeys.map(async (key) => {
            const accountType =
              userDapps[key.address] && key.accountType === ACCOUNT_CATEGORY.APP_SCOPED ? ACCOUNT_CATEGORY.APP_SCOPED_DERIVED : key.accountType;

            const address = await this.addAccount({
              privKey: key.privKey.padStart(64, "0"),
              userInfo: {
                ...userInfo,
                name: userDapps[key.address] || userInfo.name,
              },
              rehydrate: true,
              sessionPubKey: generatePubKeyFromPrivateKey(sessionId), // TODO: check if Solana needs this
              type: accountType,
              signatures: data.signatures,
              mainAddress: thresholdAccount?.address,
            });

            if (userDapps[key.address] === SOLANA_WALLET_DAPP_NAME) {
              selectedAddress = address;
            }
          })
        );
      }

      this.setSelectedAccount(selectedAddress);

      // Add dapp-scoped accounts if any, this is used by Auth dashboard
      const { userDapps: dashboardUserDapps, keys: dashboardDappsKeys } = await AuthFactory.getAuthDashboardDapps();
      const mainAddress = mainAddresses[0].address;
      await Promise.all(
        dashboardDappsKeys.map(async (key) => {
          const accountType =
            dashboardUserDapps[key.address] && key.accountType === ACCOUNT_CATEGORY.APP_SCOPED
              ? ACCOUNT_CATEGORY.APP_SCOPED_DERIVED
              : key.accountType;

          const dappAddress = await this.addAccount({
            privKey: key.privKey.padStart(64, "0"),
            userInfo: {
              ...userInfo,
              name: dashboardUserDapps[key.address] || userInfo.name,
            },
            rehydrate: true,
            type: accountType,
            signatures: data.signatures,
            mainAddress,
          });

          key.dappIds.forEach((dappId) => {
            addDashboardApplication({ dappId, address: dappAddress });
          });

          return dappAddress;
        })
      );
      this.emit(TORUS_CONTROLLER_EVENTS.LOGIN_RESPONSE, null, this.selectedAddresses, sessionId);
      return { success: true, isUserSkippedMfa: AuthFactory.isUserSkippedMfa(data.keys) };
    } catch (error) {
      this.emit(TORUS_CONTROLLER_EVENTS.LOGIN_RESPONSE, getLoginErrorMessage(error));
      log.error(error);
      throw error;
    }
  }

  public getWeb3AuthNetwork(authConnection?: AUTH_CONNECTION_TYPE): WEB3AUTH_NETWORK_TYPE {
    if (!authConnection) {
      return appConfig.web3AuthNetwork;
    }

    if (loginMode.value === LOGIN_MODE.main || this.state.EmbedControllerState.enableLegacyConfig) {
      return appConfig.web3AuthNetwork;
    }

    const authConnectionConfigItem = this.state.EmbedControllerState.authConnectionConfig?.find((x) => x.authConnection === authConnection);
    if (loginMode.value === LOGIN_MODE.embed && authConnectionConfigItem) {
      return this.state.EmbedControllerState.web3AuthNetwork;
    }

    return appConfig.web3AuthNetwork;
  }

  public getWeb3AuthClientId(authConnection?: AUTH_CONNECTION_TYPE): string {
    if (!authConnection) {
      return appConfig.web3AuthClientId;
    }

    if (loginMode.value === LOGIN_MODE.main || this.state.EmbedControllerState.enableLegacyConfig) {
      return appConfig.web3AuthClientId;
    }

    const authConnectionConfigItem = this.state.EmbedControllerState.authConnectionConfig?.find((x) => x.authConnection === authConnection);
    if (loginMode.value === LOGIN_MODE.embed && authConnectionConfigItem) {
      return this.state.EmbedControllerState.web3AuthClientId;
    }

    return appConfig.web3AuthClientId;
  }

  public getAuthConnectionConfig(): AuthConnectionConfig | undefined {
    if (loginMode.value === LOGIN_MODE.main) {
      return appConfig.authConnectionConfig;
    }

    if (loginMode.value === LOGIN_MODE.embed && this.state.EmbedControllerState.authConnectionConfig) {
      return this.state.EmbedControllerState.authConnectionConfig;
    }

    return appConfig.authConnectionConfig;
  }

  async handleWindowBlockAlert({ windowId, finalUrl }: WindowBlockAlertParams): Promise<null> {
    const { EmbedControllerState } = this.state;
    return new Promise((resolve) => {
      this.communicationWindowManager.once(`${windowId}:opened`, () => {
        resolve(null);
      });
      this.embedController.update({
        ...EmbedControllerState,
        windowBlockAlert: [...EmbedControllerState.windowBlockAlert, { windowId, finalUrl }],
      });
    });
  }

  public toggleIframeFullScreen(req?: { id?: string; show?: boolean }): void {
    const newState = !req || req.show === undefined ? !this.state.EmbedControllerState.isIFrameFullScreen : req.show;
    log.info("toggling iframe status to", newState);
    this.communicationNotificationEmitter.emit("notification", {
      method: COMMUNICATION_NOTIFICATIONS.IFRAME_STATUS,
      params: {
        isFullScreen: newState,
        rid: req?.id || undefined,
      },
    });
  }

  public closeFrame(): void {
    const id = randomId();
    const { EmbedControllerState } = this.state;
    this.embedController.update({
      ...EmbedControllerState,
      showWalletConnect: {
        show: false,
      },
      showFunding: {
        show: false,
      },
      showCheckout: {
        show: false,
      },
      showReceive: {
        show: false,
      },
      showWalletUi: {
        show: false,
      },
      showSwap: {
        show: false,
      },
      isIFrameFullScreen: false,
      oauthModalVisibility: false,
      loginInProgress: false,
    });
    this.toggleIframeFullScreen({ id, show: false });
  }

  public setShowCheckout(show: boolean): void {
    const { EmbedControllerState } = this.state;
    this.embedController.update({
      ...EmbedControllerState,
      showCheckout: show
        ? {
            ...EmbedControllerState.showCheckout,
            show: true,
          }
        : { show: false },
    });
  }

  public setShowReceive(show: boolean): void {
    const { EmbedControllerState } = this.state;
    this.embedController.update({
      ...EmbedControllerState,
      showReceive: show
        ? {
            ...EmbedControllerState.showReceive,
            show: true,
          }
        : { show: false },
    });
  }

  public toggleWalletUi(walletUiParams?: BaseEmbedControllerState["showWalletUi"]): boolean {
    const { EmbedControllerState } = this.state;
    const newShowState = walletUiParams ? walletUiParams.show : !EmbedControllerState.showWalletUi.show;
    this.embedController.update({
      ...EmbedControllerState,
      showWalletUi: newShowState ? { show: true, path: walletUiParams?.path || "" } : { show: false },
    });

    return newShowState;
  }

  public hideOAuthModal(): void {
    this.embedController.update({ oauthModalVisibility: false });
  }

  public async allowPopup(windowId: string): Promise<void> {
    const { finalUrl } = this.state.EmbedControllerState.windowBlockAlert.find((x) => x.windowId === windowId);
    const url = new URL(finalUrl);
    const handledWindow = new PopupHandler({
      config: {
        target: "_blank",
        communicationEngineNotificationManager: this.communicationNotificationEmitter,
        communicationWindowManager: this.communicationWindowManager,
        features: getPopupFeatures(FEATURES_CONFIRM_WINDOW),
        timeout: 500, // this won't be for login window. so, can handle lower timeout
        // this won't get blocked. so, no need to pass in windowBlockAlert
      },
      state: {
        url,
        windowId,
      },
    });
    handledWindow.once("close", () => {
      this.communicationWindowManager.emit(`${windowId}:closed`); // tells stream window to mark popup as closed
    });
    await handledWindow.open();
    this.communicationWindowManager.emit(`${windowId}:opened`); // Used by handleWindowBlockAlert
    // user has clicked cancel/confirm in window. can safely close now
    this.communicationWindowManager.once(`${windowId}:close`, () => {
      handledWindow.close();
    });
    this.embedController.update({
      ...this.state.EmbedControllerState,
      windowBlockAlert: this.state.EmbedControllerState.windowBlockAlert.filter((x) => x.windowId !== windowId),
    });
    if (this.state.EmbedControllerState.windowBlockAlert?.length === 0) {
      this.closeFrame();
    }
  }

  public getDappMetadata() {
    return this.embedController.state.dappMetadata;
  }

  public requestAccessTokenFromDapp(forceRefresh = false): Promise<string> {
    return new Promise((resolve, reject) => {
      const requestId = randomId();
      const timeout = setTimeout(() => {
        if (this._pendingTokenRequests.has(requestId)) {
          this._pendingTokenRequests.delete(requestId);
          reject(new Error("Access token request timed out"));
        }
      }, 30000);

      // The host dapp replies asynchronously over the communication channel, so
      // keep the requestId local to this controller until ACCESS_TOKEN_RESULT arrives.
      this._pendingTokenRequests.set(requestId, {
        resolve: (token: string) => {
          clearTimeout(timeout);
          resolve(token);
        },
        reject: (error: Error) => {
          clearTimeout(timeout);
          reject(error);
        },
      });

      this.communicationNotificationEmitter.emit("notification", {
        method: AUTH_JRPC_METHODS.NEED_ACCESS_TOKEN,
        params: { requestId, forceRefresh },
      });
    });
  }

  toggleWidgetButton(): void {
    const { whiteLabel, gating } = this.state.EmbedControllerState;
    const show = whiteLabel.showWidgetButton && gating.allowWalletService;
    this.communicationNotificationEmitter.emit("notification", {
      method: COMMUNICATION_NOTIFICATIONS.TOGGLE_WIDGET_BUTTON,
      params: {
        show,
      },
    });
  }

  allowWalletService(): void {
    const { allowWalletService } = this.state.EmbedControllerState.gating;
    this.communicationNotificationEmitter.emit("notification", {
      method: COMMUNICATION_NOTIFICATIONS.ALLOW_WALLET_SERVICE,
      params: {
        allow: allowWalletService,
      },
    });
  }

  public notifyIsLoggedIn(): void {
    const currentAuthConnection = this.getAccountPreferences(this.selectedAddress)?.userInfo.authConnection || "";
    this.communicationNotificationEmitter.emit("notification", {
      method: COMMUNICATION_NOTIFICATIONS.USER_LOGGED_IN,
      params: { currentAuthConnection },
    });
    if (!this.isRehydrated) {
      // when switching to a different chain namespace, controller is rehydrated, and user is already logged in, so we don't need to toggle the widget button
      this.toggleWidgetButton();
    }
    this.allowWalletService();
  }

  public setupCommunicationChannels(providerConnectionStream: Substream, communicationConnectionStream: Substream, originDomain: string): () => void {
    /**
     * setupUnTrustedCommunication
     * Used to create a multiplexed stream for connecting to an untrusted context
     * like a Dapp or other extension.
     */
    const provider = this.setupProviderConnection(providerConnectionStream, originDomain);
    const communicationProvider = this.setupCommunicationProviderConnection(communicationConnectionStream, originDomain);

    const cancel = () => {
      provider.removeAllListeners();
      communicationProvider.removeAllListeners();
    };
    return cancel;
  }

  public setHttpClient(auth: Auth): void {
    this.httpClient = this.createHttpClient(auth);
  }

  /**
   * Tears down resources that should not survive the current user session.
   * Subclasses should override this to dispose chain-specific controllers and
   * MUST call `super.endSession()` to retain base cleanup.
   */
  public endSession(): void {
    // Reject and clear any in-flight plugin-mode access token requests so their
    // timers and promise callbacks don't outlive the session.
    this._pendingTokenRequests.forEach((pending) => {
      pending.reject(new Error("User logged out"));
    });
    this._pendingTokenRequests.clear();
  }

  protected checkWalletServicesAccess({ isShowWalletUi = false }: { isShowWalletUi?: boolean } = {}) {
    // on plugin mode, always allow access to all actions except showWalletUI
    if (loginMode.value === LOGIN_MODE.plugin && !isShowWalletUi) return;
    // for other modes, check if wallet services access is allowed
    if (!this.state.EmbedControllerState.gating.allowWalletService) {
      throw new Error(this.state.EmbedControllerState.gating.errorMessage);
    }
  }

  protected initializeCommunicationProvider() {
    const commProviderHandlers: ICommunicationProviderHandlers = {
      loginWithPrivateKey: () => {
        throw new Error("Unsupported method");
      },
      showTransfer: () => {
        throw new Error("Unsupported method");
      },
      loginWithSessionId: async (req: JRPCRequest<[string, string]>) => {
        try {
          const [sessionString, idToken] = req.params;
          setLoginMode(LOGIN_MODE.plugin);
          const response = await this.connectWithSession(sessionString, idToken);
          return response;
        } catch (error) {
          log.error(error);
          setLoginMode(LOGIN_MODE.embed);
          throw error;
        }
      },
      setIFrameStatus: this.setIFrameStatus.bind(this),
      changeProvider: (_req: JRPCRequest<unknown>) => {
        throw new Error("Unsupported method. Please use wallet_switchChain instead");
      },
      logout: this.logout.bind(this),
      getUserInfo: () => {
        return {
          email: this.userInfo.email,
          name: this.userInfo.name,
          profileImage: this.userInfo.profileImage,
          groupedAuthConnectionId: this.userInfo.groupedAuthConnectionId,
          authConnectionId: this.userInfo.authConnectionId,
          userId: this.userInfo.userId,
          authConnection: this.userInfo.authConnection,
        };
      },
      getWalletInstanceId: () => {
        return "";
      },
      topup: async () => {
        return false;
      },
      handleWindowRpc: this.communicationWindowManager.handleWindowRpc,
      getProviderState: () => {
        return {
          currentAuthConnection: this.getAccountPreferences(this.selectedAddress)?.userInfo.authConnection as AUTH_CONNECTION_TYPE,
          isLoggedIn: !!this.selectedAddress,
        };
      },
      showWalletConnect: ({ request }) => {
        this.checkWalletServicesAccess();
        const showWalletConnectParams = request.params;
        const { EmbedControllerState } = this.state;
        const newShowState = showWalletConnectParams ? showWalletConnectParams.show : !EmbedControllerState.showWalletConnect.show;
        this.embedController.update({
          ...EmbedControllerState,
          showWalletConnect: { show: newShowState },
        });
        return newShowState;
      },
      showWalletUi: ({ request }) => {
        this.checkWalletServicesAccess({ isShowWalletUi: true });
        const showWalletUiParams = request.params;
        const newShowState = this.toggleWalletUi(showWalletUiParams);
        return newShowState;
      },
      showFunding: ({ request }) => {
        this.checkWalletServicesAccess();
        const showFundingParams = request.params as { show: boolean } & ShowFundingParams;
        const { EmbedControllerState } = this.state;
        const newShowState = showFundingParams ? showFundingParams.show : !EmbedControllerState.showFunding.show;

        const fundingMethods = ["hideTopup", "hideReceive"] as const;
        const allFundingMethodsHidden = fundingMethods.every((method) => EmbedControllerState.whiteLabel[method] === true);
        if (allFundingMethodsHidden) {
          throw new Error(`At least one of the following whiteLabel params should be set to false: ${fundingMethods.join(", ")}`);
        }

        const updatedState = {
          ...EmbedControllerState,
          showFunding: { show: newShowState },
        };

        if (showFundingParams?.checkoutParams) {
          updatedState.showCheckout = {
            show: false,
            receiveWalletAddress: showFundingParams.checkoutParams.receiveWalletAddress || "",
            tokenList: showFundingParams.checkoutParams.tokenList || [],
            fiatList: showFundingParams.checkoutParams.fiatList || [],
            crypto: showFundingParams.checkoutParams.crypto || "",
            fiat: showFundingParams.checkoutParams.fiat || "",
            fiatAmount: showFundingParams.checkoutParams.fiatAmount || "",
            cryptoAmount: showFundingParams.checkoutParams.cryptoAmount || "",
          };
        }

        this.embedController.update(updatedState);
        return newShowState;
      },
      showCheckout: ({ request }) => {
        this.checkWalletServicesAccess();
        const showCheckoutParams = request.params as BaseEmbedControllerState["showCheckout"];
        const { EmbedControllerState, NetworkControllerState } = this.state;
        if (NetworkControllerState.providerConfig.isTestnet) {
          throw new Error("Checkout not supported on testnets");
        }

        const newShowState = showCheckoutParams ? showCheckoutParams.show : !EmbedControllerState.showCheckout.show;
        this.embedController.update({
          ...EmbedControllerState,
          showCheckout: newShowState
            ? {
                show: true,
                receiveWalletAddress: showCheckoutParams?.receiveWalletAddress || "",
                tokenList: showCheckoutParams?.tokenList || [],
                fiatList: showCheckoutParams?.fiatList || [],
                crypto: showCheckoutParams?.crypto || "",
                fiat: showCheckoutParams?.fiat || "",
                fiatAmount: showCheckoutParams?.fiatAmount || "",
                cryptoAmount: showCheckoutParams?.cryptoAmount || "",
              }
            : { show: false },
        });
        return newShowState;
      },
      showReceive: ({ request }) => {
        this.checkWalletServicesAccess();
        const showReceiveParams = request.params;
        const { EmbedControllerState } = this.state;

        const newShowState = showReceiveParams ? showReceiveParams.show : !EmbedControllerState.showReceive.show;
        this.embedController.update({
          ...EmbedControllerState,
          showReceive: {
            show: newShowState,
          },
        });
        return newShowState;
      },
      showSwap: ({ request }) => {
        this.checkWalletServicesAccess();
        const showSwapParams = request.params;
        const { EmbedControllerState, NetworkControllerState } = this.state;
        if (!isSwapAllowed(NetworkControllerState.providerConfig.chainId)) {
          throw new Error(`Swap not supported on selected network ${NetworkControllerState.providerConfig.chainId}`);
        }
        const newShowState = showSwapParams ? showSwapParams.show : !EmbedControllerState.showSwap.show;
        this.embedController.update({
          ...EmbedControllerState,
          showSwap: newShowState
            ? {
                show: true,
                fromToken: showSwapParams?.fromToken || "",
                fromValue: showSwapParams?.fromValue || "",
                toToken: showSwapParams?.toToken || "",
                toAddress: showSwapParams?.toAddress || "",
              }
            : { show: false },
        });
        return newShowState;
      },
      showWindowBlockAlert: ({ request }) => {
        const params = request.params;
        const { EmbedControllerState } = this.state;
        this.embedController.update({
          ...EmbedControllerState,
          windowBlockAlert: [...EmbedControllerState.windowBlockAlert, { ...params }],
        });
        return true;
      },
    };
    this.embedController.initializeProvider(commProviderHandlers);
  }

  protected async handleSignMessageModal({
    messageData,
    type = SIGN_CONFIRM.MESSAGE,
    isModal = true,
  }: {
    messageData: MessageChannelEventData;
    type?: SIGN_CONFIRM_TYPE;
    isModal?: boolean;
  }): Promise<boolean> {
    const rid = randomId();

    this.emit(isModal ? TORUS_CONTROLLER_EVENTS.CONFIRMATION_MODAL_SHOW : TORUS_CONTROLLER_EVENTS.CONFIRMATION_VIEW_SHOW, { messageData, rid, type });
    return new Promise((resolve) => {
      this.once(
        `${isModal ? TORUS_CONTROLLER_EVENTS.CONFIRMATION_MODAL_RESULT : TORUS_CONTROLLER_EVENTS.CONFIRMATION_VIEW_RESULT}_${rid}`,
        (isApproved: boolean) => {
          resolve(isApproved);
        }
      );
    });
  }

  protected async handleSignMessagePopup({
    messageData,
    windowId,
    type = SIGN_CONFIRM.MESSAGE,
  }: {
    messageData: MessageChannelEventData;
    windowId: string;
    type?: SIGN_CONFIRM_TYPE;
  }): Promise<{ approve: boolean; updatedTxData?: GasFeeModalData }> {
    try {
      const path = type === SIGN_CONFIRM.MESSAGE ? "confirm-message" : "confirm-transaction";
      let finalWindowId = windowId;
      if (!windowId) finalWindowId = randomId();
      const finalUrl = new URL(`${appConfig.baseRoute}${path}?windowId=${finalWindowId}&sessionNamespace=${rootSessionNamespace.value}`);
      const instanceId = await getSessionIdFromStorage();
      const msgApproveWindow = new PopupWithBcHandler<{ approve: boolean; updatedTxData?: GasFeeModalData }, MessageChannelEventData>({
        state: {
          url: finalUrl,
          windowId: finalWindowId,
        },
        config: {
          communicationEngine: this.communicationEngine,
          communicationWindowManager: this.communicationWindowManager,
          communicationEngineNotificationManager: this.communicationNotificationEmitter,
          target: "_blank",
          features: getPopupFeatures(FEATURES_CONFIRM_WINDOW),
          handleWindowBlockAlert: this.handleWindowBlockAlert.bind(this),
          instanceId,
          timeout: 0, // these windows are already pre-opened, no need for delay to emit closed event
          isWindowAlreadyOpen: !!windowId,
        },
        channelPrefix: BROADCAST_CHANNELS.MESSAGE_CHANNEL,
      });

      // BroadcastChannel does not support BigInt, need to convert it to string
      const payloadStr = JSON.stringify(messageData, (_, v) => (typeof v === "bigint" ? v.toString() : v));
      const convertedPayload = JSON.parse(payloadStr);
      const result = await msgApproveWindow.handleWithHandshake(convertedPayload);
      return result;
    } catch (error) {
      log.error(error);
      return { approve: false };
    }
  }

  protected handleLogout(): void {
    this.emitLogoutEvent();
    this.providerNotificationEmitter.emit("notification", {
      method: PROVIDER_NOTIFICATIONS.ACCOUNTS_CHANGED,
      params: [],
    });
    this.providerNotificationEmitter.emit("notification", {
      method: PROVIDER_NOTIFICATIONS.UNLOCK_STATE_CHANGED,
      params: {
        accounts: [],
        isUnlocked: false,
      },
    });
    this.communicationNotificationEmitter.emit("notification", {
      method: COMMUNICATION_NOTIFICATIONS.USER_LOGGED_OUT,
    });
  }

  protected emitLogoutEvent() {
    this.emit(TORUS_CONTROLLER_EVENTS.LOGOUT);
  }

  protected async requestAccounts(req: JRPCRequest<unknown>): Promise<string[]> {
    const theme = await getUserThemeFromStorage();
    return new Promise((resolve, reject) => {
      const [requestedAuthConnection, loginHint, requestedAuthConnectionId, requestedGroupedAuthConnectionId, preOpenedWindowId] =
        req.params as string[];
      const currentAuthConnection = this.getAccountPreferences(this.selectedAddress)?.userInfo.authConnection;
      if (requestedAuthConnection) {
        if (requestedAuthConnection === currentAuthConnection && this.selectedAddress) {
          if (preOpenedWindowId) {
            this.communicationNotificationEmitter.emit("notification", {
              method: COMMUNICATION_NOTIFICATIONS.CLOSE_WINDOW,
              params: { windowId: preOpenedWindowId },
            });
          }
          return resolve(this.selectedAddresses);
        } else {
          // To login with the requested provider
          // On Embed, we have a window waiting... we need to tell it to login
          this.embedController.update({ loginInProgress: true, oauthModalVisibility: false });
          this.triggerLogin({
            authConnection: requestedAuthConnection as AUTH_CONNECTION_TYPE,
            ...(requestedAuthConnectionId ? { authConnectionId: requestedAuthConnectionId } : {}),
            ...(requestedGroupedAuthConnectionId ? { groupedAuthConnectionId: requestedGroupedAuthConnectionId } : {}),
            login_hint: loginHint,
            theme: this.state.EmbedControllerState.whiteLabel?.mode || (theme as THEME_MODE_TYPE) || THEME_MODES.light,
            preOpenedWindowId, // Pass the pre-opened window ID from ws-embed
          });
          this.once(TORUS_CONTROLLER_EVENTS.LOGIN_RESPONSE, (error: string, addresses: string[]) => {
            if (error) reject(new Error(error));
            else resolve(addresses);
            this.embedController.update({ loginInProgress: false, oauthModalVisibility: false });
          });
        }
      } else if (this.selectedAddress) resolve(this.selectedAddresses);
      else {
        // We show the modal to login
        this.embedController.update({ loginInProgress: true, oauthModalVisibility: true });
        this.once(TORUS_CONTROLLER_EVENTS.LOGIN_RESPONSE, (error: string, addresses: string[]) => {
          if (error) reject(new Error(error));
          else resolve(addresses);
          this.embedController.update({ loginInProgress: false, oauthModalVisibility: false });
        });
      }
    });
  }

  protected async handleConfirmation(
    req: JRPCRequest<unknown> &
      UserRequestApprovalParams & {
        isWalletConnectRequest?: boolean;
        isWalletUiRequest?: boolean;
        platform?: APP_PLATFORMS_TYPE;
        isAAProviderRequest?: boolean;
      },
    messageData: MessageChannelEventData["msgParams"],
    onSuccess: (id: string, updatedTxData?: GasFeeModalData) => Promise<unknown>,
    onReject: (id: string) => void,
    onFailure: (error: Error) => void,
    type: SIGN_CONFIRM_TYPE = SIGN_CONFIRM.MESSAGE
  ): Promise<void> {
    try {
      const mutableRequet = cloneDeep(req);
      const messageDataPayload = await this.getMessageDataPayload(mutableRequet, messageData);
      const { confirmationStrategy = CONFIRMATION_STRATEGY.DEFAULT } = this.state.EmbedControllerState;

      // Use the confirmation strategy from embed state
      let confirmationStrategyFinal = confirmationStrategy;

      // Use modal confirmation strategy if request is coming platform, wallet connect
      const forceModalStrategy =
        req.isWalletConnectRequest || Object.values(APP_PLATFORMS).includes(req.platform) || confirmationStrategy === CONFIRMATION_STRATEGY.MODAL;

      /**
       * Main & Plugin Mode
       * Use auto approve unless forceModalStrategy is true
       */
      if (isMain || loginMode.value === LOGIN_MODE.plugin) {
        confirmationStrategyFinal = forceModalStrategy ? CONFIRMATION_STRATEGY.MODAL : CONFIRMATION_STRATEGY.AUTO_APPROVE;
      }

      /**
       * Embed
       * if forceModalStrategy is true use modal
       * if confirmationStrategy is default use popup
       */
      if (loginMode.value === LOGIN_MODE.embed) {
        if (forceModalStrategy) confirmationStrategyFinal = CONFIRMATION_STRATEGY.MODAL;
        else if (confirmationStrategy === CONFIRMATION_STRATEGY.DEFAULT || confirmationStrategy === CONFIRMATION_STRATEGY.AUTO_APPROVE)
          confirmationStrategyFinal = CONFIRMATION_STRATEGY.POPUP;
      }

      // Use auto approve strategy if action is triggered from wallet UI
      const forceAutoApproveStrategy = req.isWalletUiRequest || req.isAAProviderRequest;
      if (forceAutoApproveStrategy) confirmationStrategyFinal = CONFIRMATION_STRATEGY.AUTO_APPROVE;

      // if messageData is an array, use the first id
      const messageId = Array.isArray(messageData) ? messageData[0].id : messageData.id;
      if (confirmationStrategyFinal === CONFIRMATION_STRATEGY.AUTO_APPROVE) {
        await onSuccess(messageId);
        return;
      }
      if (confirmationStrategyFinal === CONFIRMATION_STRATEGY.MODAL) {
        const isApproved = await this.handleSignMessageModal({ messageData: messageDataPayload, type, isModal: true });
        if (isApproved) {
          await onSuccess(messageId);
        } else {
          onReject(messageId);
        }
        return;
      }

      const { approve, updatedTxData } = await this.handleSignMessagePopup({ messageData: messageDataPayload, windowId: req.windowId, type });
      if (approve) {
        await onSuccess(messageId, updatedTxData);
      } else {
        onReject(messageId);
      }
    } catch (error) {
      log.error(error);
      onFailure(error as Error);
    }
  }

  protected async notifyChainChanged() {
    this.providerChainChangedConfig = {
      chainId: this.state.NetworkControllerState.providerConfig.chainId,
      chainNamespace: this.state.NetworkControllerState.providerConfig.chainNamespace,
    };
    this.emit(CONTROLLER_PROVIDER_CHAIN_CHANGED_EVENT);
    // Check and wait until selected account is set is completed
    if (!this.selectedAddress) {
      await new Promise((resolve) => {
        this.providerNotificationEmitter.once("notification", (event) => {
          const { method } = event as { method: string };
          if (method === PROVIDER_NOTIFICATIONS.ACCOUNTS_CHANGED) {
            resolve(true);
          }
        });
      });
    }
    this.providerNotificationEmitter.emit("notification", {
      method: PROVIDER_NOTIFICATIONS.CHAIN_CHANGED,
      params: {
        chainId: this.state.NetworkControllerState.providerConfig.chainId,
      },
    });
  }

  protected async getIdToken(): Promise<string> {
    return AuthFactory.getIdToken();
  }

  protected getAuthAccessToken(): Promise<string> {
    return AuthFactory.getAccessToken();
  }

  protected getAuthHttpClient() {
    return this.httpClient;
  }

  /**
   * Validate the switch chain data against current controller's chain config and return the chainId value
   * @param data - The switch chain data
   * @returns The chainId value
   */
  protected validateSwitchChainData(data: { chainId: string }): string {
    let chainIdValue: string;
    if (isStrictHexString(data.chainId) && data.chainId.length > 2) {
      // for eth_switchEthereumChain, the chainId is in hex format
      // we assume that chainId is EVM chainId
      chainIdValue = data.chainId;
    } else {
      // for chain agnostic wallet_switchChain, the chainId is in the format of chainNamespace:chainId
      const { chainId: fullChainId } = data;
      const [incomingNamespace, incomingChainId] = fullChainId.split(":");
      if (incomingNamespace === CHAIN_NAMESPACES.EIP155) {
        chainIdValue = `0x${Number(incomingChainId).toString(16)}`;
      } else if (incomingNamespace === CHAIN_NAMESPACES.SOLANA) {
        chainIdValue = fullChainId;
      } else {
        throw new Error(`Unsupported chain namespace: ${incomingNamespace}`);
      }
    }

    return chainIdValue;
  }

  protected handleProviderError(error: unknown) {
    this.emit(CONTROLLER_PROVIDER_ERROR_EVENT, error);
    log.error("error in providerError", error);
  }

  private logout(): boolean {
    this.handleLogout();
    return true;
  }

  private async setIFrameStatus(request: JRPCRequest<SetIFrameStatusParams>): Promise<boolean> {
    const { isIFrameFullScreen = false, rid } = request.params || {};
    log.info("setting iframe status from embed", request.params, rid);
    this.embedController.update({
      isIFrameFullScreen,
    });
    if (isIFrameFullScreen) {
      this.idleTimeTracker.markActive();
    } else {
      this.idleTimeTracker.markIdle();
    }
    if (rid) {
      this.emit(rid);
      this.communicationWindowManager.emit(`${rid}:${isIFrameFullScreen ? "iframe-opened" : "iframe-closed"}`);
    }
    return isIFrameFullScreen;
  }

  /**
   * A method for serving our provider over a given stream.
   */
  private setupCommunicationProviderConnection(providerStream: Substream, sender: string) {
    // break violently
    const senderUrl = new URL(sender);

    const engine = this.setupCommunicationProviderEngine({ origin: senderUrl.origin });
    this.communicationEngine = engine;
    // setup connection
    const engineStream = createEngineStreamV2({ engine, notificationEmitter: this.communicationNotificationEmitter });

    return pipeline(providerStream, engineStream, providerStream, (error: Error | undefined) => {
      if (this.communicationEngine === engine) this.communicationEngine = undefined;
      if (error) {
        // cleanup filter polyfill middleware
        this.communicationEngine = undefined;
        log.error(error);
      }
    });
  }

  /**
   * A method for serving our provider over a given stream.
   */
  private setupProviderConnection(providerStream: Substream, sender: string) {
    // break violently
    const senderUrl = new URL(sender);

    const engine = this.setupProviderEngine({ origin: senderUrl.origin });
    log.info("initializing preferences controller with hostname", sender, senderUrl.origin);

    this.setOrigin(senderUrl.origin);
    this.engine = engine;

    // setup connection
    const engineStream = createEngineStreamV2({ engine, notificationEmitter: this.providerNotificationEmitter });

    return pipeline(providerStream, engineStream, providerStream, (error: Error | undefined) => {
      if (error) {
        // cleanup filter polyfill middleware
        this.engine = undefined;
        log.error(error);
      }
    });
  }

  /**
   * A method for creating a provider that is safely restricted for the requesting domain.
   * */
  private setupProviderEngine({ origin }: { origin: string }): JRPCEngineV2<JsonRpcCall> {
    const providerProxy = this.provider;
    // setup json rpc engine stack
    const engine = JRPCEngineV2.create({
      middleware: [
        createOriginMiddleware({ origin }),
        createLoggerMiddleware({ origin }),
        createIdleTimeTrackerMiddleware({
          idleTimeTracker: this.idleTimeTracker,
          isSessionActive: () => !!this.selectedAddress,
        }),
        providerAsMiddlewareV2(providerProxy),
      ],
    }) as JRPCEngineV2<JsonRpcCall>;

    return engine;
  }

  /**
   * A method for creating a provider that is safely restricted for the requesting domain.
   * */
  private setupCommunicationProviderEngine({ origin }: { origin: string }): JRPCEngineV2<JsonRpcCall> {
    const { _communicationProviderProxy: communicationProviderProxy } = this.embedController;
    // setup json rpc engine stack
    const engine = JRPCEngineV2.create({
      middleware: [
        createOriginMiddleware({ origin }),
        createLoggerMiddleware({ origin }),
        createIdleTimeTrackerMiddleware({
          idleTimeTracker: this.idleTimeTracker,
          isSessionActive: () => !!this.selectedAddress,
        }),
        this.createAccessTokenResultMiddleware(),
        providerAsMiddlewareV2(communicationProviderProxy),
      ],
    }) as JRPCEngineV2<JsonRpcCall>;
    // const { _providerProxy } = this.networkController;

    // create subscription polyfill middleware
    // const subscriptionManager = createSubscriptionManager({ provider, blockTracker });
    // subscriptionManager.events.on("notification", (message) => engine.emit("notification", message));

    return engine;
  }

  private createHttpClient(auth: Auth): HttpClient {
    if (loginMode.value === LOGIN_MODE.plugin) {
      return new HttpClient(new TokenProviderAdapter((forceRefresh) => this.requestAccessTokenFromDapp(forceRefresh)));
    }
    return new HttpClient(auth.authSessionManager);
  }

  private createAccessTokenResultMiddleware(): JRPCMiddlewareV2<JsonRpcCall> {
    return ({ request, next }) => {
      if (request.method === AUTH_JRPC_METHODS.ACCESS_TOKEN_RESULT) {
        const params = request.params as { requestId: string; accessToken?: string; error?: string };
        // This must resolve against the same controller instance that created
        // the requestId above, otherwise the pending map will be empty.
        const pending = this._pendingTokenRequests.get(params.requestId);
        if (pending) {
          this._pendingTokenRequests.delete(params.requestId);
          if (params.error) {
            pending.reject(new Error(params.error));
          } else {
            pending.resolve(params.accessToken);
          }
        }
        return { success: true };
      }
      return next();
    };
  }

  public abstract addAccount(params: {
    privKey: string;
    userInfo: UserInfo;
    type: ACCOUNT_TYPE;
    rehydrate?: boolean;
    sessionPubKey?: string;
    loginMode?: LOGIN_MODE_TYPE;
    signatures?: string[];
    mainAddress?: string;
  }): Promise<string>;

  public abstract setSelectedAccount(address: string): void;

  public abstract setOrigin(origin: string): void;

  protected abstract getAccountPreferences(address: string): AddressPreferences | undefined;

  protected abstract getMessageDataPayload(
    req: JRPCRequest<unknown> & UserRequestApprovalParams & { isWalletConnectRequest?: boolean },
    messageData: MessageChannelEventData["msgParams"]
  ): Promise<MessageChannelEventData>;
}
